// @ts-nocheck
/* eslint-disable spire/avoid-dynamic-translate */
import { Typography } from "@insite/mobius/index";
import { createTableData, TotalCostData } from "@store/Framework/Common/Utilities/reportingUtils";
import DataTableSearchForm from "@store/Library/Components/Reporting/ApiDataTable/DataTableSearchForm";
import ReportDataTable, {
    DataTableData,
    FormatDictionary,
} from "@store/Library/Components/Reporting/ApiDataTable/ReportDataTable";
import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";

import translate from "@insite/client-framework/Translate";
import getColor from "@insite/mobius/utilities/getColor";
import useLocalizeCurrency from "@store/Framework/Common/Hooks/useLocalizeCurrency";
import useProductContextsBySkus from "@store/Framework/Common/Hooks/useProductContextsBySku";
import { MonthlyExpenseComparisonItemModel } from "@store/Framework/Services/Reporting/ReportingModels";
import AddToCartAndWishList from "@store/Library/Components/Reporting/AddToCartAndWishList/AddtoCartAndWishList";
import { NamedMonthlyExpenseComparisonItemModel } from "@store/Library/Widgets/Reports/MonthlyExpenseSearchForm";
import useReportingProductLinkFormatting from "@store/Library/Widgets/Reports/useReportingLinkFormatting";

export const TableHeader = styled.div`
    width: 100%;
    margin-top: 1rem;
    display: inline-flex;
    padding: 0.5rem 1rem;
    background-color: rgb(13, 44, 107);
    border-radius: 0.25rem;
    margin-bottom: 3rem;
    align-items: center;
    & p {
        margin-bottom: 0;
    }
`;
const styles = {
    Typography: {
        css: css`
            color: ${getColor("common.background")};
        `,
    },
};
const regexForSku = /([^\s]+)/;

type TotalCostDataWithElements = TotalCostData & { extraElements?: JSX.Element };
interface Props {
    monthlyExpenseData: NamedMonthlyExpenseComparisonItemModel[];
    title: string;
    excelDataKeys: readonly (keyof NamedMonthlyExpenseComparisonItemModel)[];
    excelHeaders: string[];
    sharedSearchTerm: string;
    setSharedSearchTerm: (value: string) => void;
}
const MemoizedDataTable = React.memo(ReportDataTable);
const MonthlyExpenseTable: React.FC<Props> = ({
    excelHeaders,
    excelDataKeys,
    monthlyExpenseData,
    title,
    sharedSearchTerm,
    setSharedSearchTerm,
}) => {
    const [filteredCostData, setFilteredCostData] = useState<TotalCostDataWithElements[]>([]);
    const [filteredQuantityData, setFilteredQuantityData] = useState<TotalCostDataWithElements[]>([]);
    const [costTableData, setCostTableData] = useState<DataTableData<any>>();
    const [quantityTableData, setQuantityTableData] = useState<DataTableData<any>>();
    const { productContextsBySku, getProductContextsBySkus, isLoading } = useProductContextsBySkus();
    const localizeCurrency = useLocalizeCurrency();
    const { getProductLinkFieldByName, getProductLinkBySku } = useReportingProductLinkFormatting(
        "itemdesc",
        "name",
        monthlyExpenseData,
        productContextsBySku,
    );

    useEffect(() => {
        const { totCostRows, quantityRows, headerKeys, dataHeaders } = createTableData(monthlyExpenseData);

        const skus = monthlyExpenseData.map(item => regexForSku.exec(item.name)?.[0] ?? "");
        getProductContextsBySkus(skus);

        const elements: JSX.Element[] = monthlyExpenseData.map(item => {
            // sku is the first set of numbers in the name field before space
            const sku = regexForSku.exec(item.name)?.[0] ?? "";
            return <AddToCartAndWishList productContextsBySku={productContextsBySku} key={sku} sku={sku} />;
        });

        const totCostRowsWithElements = totCostRows.map((row, index) => {
            return { ...row, extraElement: elements[index] };
        });
        const quantityRowsWithElements = quantityRows.map((row, index) => {
            return { ...row, extraElement: elements[index] };
        });
        const headerKeysToNotFormat = ["name", "itemdesc", "unitofmeasure", "extraElement"];
        const headerKeysToFormat = headerKeys.filter(key => !headerKeysToNotFormat.includes(String(key)));
        const formatDictionary = headerKeysToFormat.reduce((acc, curr) => {
            acc[curr] = localizeCurrency;
            return acc;
        }, {} as FormatDictionary);

        setFilteredCostData(totCostRowsWithElements);
        setFilteredQuantityData(quantityRowsWithElements);
        setCostTableData({
            data: totCostRowsWithElements,
            dataKeys: headerKeys,
            title: translate("Items by Cost"),
            headers: dataHeaders,
            dataSummaryTitle: translate("Items"),
            formatDictionary,
        });
        setQuantityTableData({
            data: quantityRowsWithElements,
            dataKeys: headerKeys,
            dataSummaryTitle: translate("Items"),
            title: translate("Items by Quantity"),
            headers: dataHeaders,
        });
    }, [monthlyExpenseData?.length]);

    useEffect(() => {
        if (!isLoading) {
            const elements: JSX.Element[] = monthlyExpenseData.map(item => {
                // sku is the first set of numbers in the name field before space
                const sku = regexForSku.exec(item.name)?.[0] ?? "";

                return <AddToCartAndWishList productContextsBySku={productContextsBySku} key={sku} sku={sku} />;
            });
            const dataWithElements = costTableData?.data.map((row, index) => {
                return { ...row, extraElement: elements[index] };
            });
            const quantityDataWithElements = quantityTableData?.data.map((row, index) => {
                return { ...row, extraElement: elements[index] };
            });
            const filteredCostDataWithElements = filteredCostData.map((row, index) => {
                return { ...row, extraElement: elements[index] };
            });
            const filteredQuantityDataWithElements = filteredQuantityData.map((row, index) => {
                return { ...row, extraElement: elements[index] };
            });

            setFilteredCostData(filteredCostDataWithElements ?? []);
            setFilteredQuantityData(filteredQuantityDataWithElements ?? []);
            if (costTableData && dataWithElements) {
                setCostTableData({
                    ...costTableData,
                    data: dataWithElements,
                    formatDictionary: {
                        ...costTableData.formatDictionary,
                        itemdesc: getProductLinkFieldByName,
                        name: getProductLinkBySku,
                    },
                });
            }
            if (quantityTableData && quantityDataWithElements) {
                setQuantityTableData({
                    ...quantityTableData,
                    data: quantityDataWithElements,
                    // because we're setting this as state its important that we set the formatting dictionary functions for the links here as doing it when we originally create the table data will form a closure
                    // on the original productContextsBySku which will be empty

                    formatDictionary: {
                        ...quantityTableData.formatDictionary,
                        itemdesc: getProductLinkFieldByName,
                        name: getProductLinkBySku,
                    },
                });
            }
        }
    }, [isLoading, monthlyExpenseData?.length]);

    const filteredQuantityTableData = React.useMemo(() => {
        return { ...quantityTableData, data: filteredQuantityData } as DataTableData<any>;
    }, [filteredQuantityData, quantityTableData]);
    const filteredCostTableData = React.useMemo(() => {
        return { ...costTableData, data: filteredCostData } as DataTableData<any>;
    }, [filteredCostData, costTableData]);

    // array as minor hack to have sheet name be different than file name
    const filteredCostExcelData = React.useMemo(
        () =>
            costTableData
                ? [
                      {
                          ...filteredCostTableData,
                          data: filteredCostData,
                          dataKeys: [...excelDataKeys, ...filteredCostTableData.dataKeys],
                          headers: [...excelHeaders, ...filteredCostTableData.headers],
                          dataSummaryTitle: translate("Monthly Expense Report"),
                      },
                  ]
                : undefined,
        [filteredCostData, filteredCostTableData],
    );
    const filteredQuantityExcelData = React.useMemo(
        () =>
            quantityTableData
                ? [
                      {
                          ...filteredQuantityTableData,
                          data: filteredQuantityData,
                          dataSummaryTitle: translate("Monthly Expense Report"),
                          dataKeys: [...excelDataKeys, ...filteredCostTableData.dataKeys],
                          headers: [...excelHeaders, ...filteredCostTableData.headers],
                      },
                  ]
                : undefined,
        [filteredQuantityData, filteredQuantityTableData],
    );

    if (costTableData && quantityTableData) {
        return (
            <>
                <TableHeader>
                    <Typography variant="h4" {...styles.Typography}>
                        {`${title} ${costTableData.title}`}
                    </Typography>
                </TableHeader>
                <DataTableSearchForm
                    dataTableData={costTableData}
                    searchTerm={sharedSearchTerm}
                    setSearchTerm={setSharedSearchTerm}
                    setFilteredData={setFilteredCostData}
                />
                <MemoizedDataTable
                    searchable={false}
                    excelData={filteredCostExcelData}
                    dataTableData={filteredCostTableData}
                />

                <TableHeader>
                    <Typography variant="h4" {...styles.Typography}>
                        {`${title} ${quantityTableData.title}`}
                    </Typography>
                </TableHeader>
                <DataTableSearchForm
                    dataTableData={quantityTableData}
                    searchTerm={sharedSearchTerm}
                    setSearchTerm={setSharedSearchTerm}
                    setFilteredData={setFilteredQuantityData}
                />
                <MemoizedDataTable
                    excelData={filteredQuantityExcelData}
                    searchable={false}
                    dataTableData={filteredQuantityTableData}
                />
            </>
        );
    }
    return <></>;
};

export default MonthlyExpenseTable;
